import React, { useCallback, useRef } from 'react';

import { OnAgentThinking } from '@/components/core';
import { ChatContainer } from '@/components/features/ChatContainer';
import { TurnAssetDrawer } from '@/components/features/ChatContainer/ui/TurnAssetDrawer';
import { ChatSessionSwitcher } from '@/components/features/ChatSessionSwitcher';
import { PromptInputWithActions } from '@/components/features/PromptInputWithActions';
import type { VoiceCallControl } from '@/components/features/PromptInputWithActions/VoiceCallBar';
import { AgentChatType } from '@/constant/chatConfig';
import { useAgentChat } from '@/hooks/useAgentChat';
import { useConnieChat } from '@/hooks/useConnieChat';
import { useConnieVoiceCall } from '@/hooks/useConnieVoiceCall';
import { useAgentPromptCommands } from '@/store/agents';
import { ChatType } from '@/store/chats';
import { User } from '@/types/auth';
import { CHAT_MESSAGE_RATING, ChatMessage, Session } from '@/types/chat';

// Stable identity keeps memoised message rows from re-rendering per token.
const NO_SUGGESTIONS: never[] = [];

interface CustomLangflowChatProps {
  sessions: Session[];
  isSessionsMode?: boolean;
  sessionId?: string;
  jobId?: string;
  chatType?: AgentChatType;
  contactId?: string;
  currentJobReference?: string;
  user: User | null;
  onMessage?: (message: ChatMessage) => void;
  onThinking: OnAgentThinking;
  onSessionChangeClick?: (session: Session) => void;
  onReturnToChatClick?: () => void;
  isActive?: boolean;
}

type ChatShellProps = {
  sessions: Session[];
  isSessionsMode?: boolean;
  sessionId?: string;
  user: User | null;
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (message: string) => void;
  updateMessageRating: (messageId: string, rating: CHAT_MESSAGE_RATING) => void;
  aiAgentId?: number;
  showFeedback: boolean;
  onLastMessageProcessCompleted?: () => void;
  onSessionChangeClick?: (session: Session) => void;
  onReturnToChatClick?: () => void;
  enableVoice?: boolean;
  onSendVoiceNote?: (blob: Blob) => void;
  voiceCall?: VoiceCallControl;
  onAbort?: () => void;
  isAutoScrollActive?: boolean;
  /** Connie's working panel on assistant turns (see AssistantMessage). */
  workingPanel?: boolean;
};

const ChatShell: React.FC<ChatShellProps> = ({
  sessions,
  isSessionsMode,
  sessionId,
  user,
  messages,
  isLoading,
  sendMessage,
  updateMessageRating,
  aiAgentId,
  showFeedback,
  onLastMessageProcessCompleted,
  onSessionChangeClick,
  onReturnToChatClick,
  enableVoice,
  onSendVoiceNote,
  voiceCall,
  onAbort,
  isAutoScrollActive,
  workingPanel,
}) => {
  const { data: agentPromptCommands, isLoading: isAgentPromptCommandsLoading } =
    useAgentPromptCommands(aiAgentId);

  return (
    <ChatSessionSwitcher
      isSessionsMode={isSessionsMode}
      sessions={sessions}
      onSessionChangeClick={onSessionChangeClick}
      onReturnToChatClick={onReturnToChatClick}
      sessionListClassName='top-0 z-[9999]'
    >
      <ChatContainer
        user={user}
        sessionId={sessionId}
        onMessageRatingChange={updateMessageRating}
        messages={messages}
        onLastMessageProcessCompleted={onLastMessageProcessCompleted}
        className='overflow-y-auto'
        possibleCommands={agentPromptCommands?.allItems ?? NO_SUGGESTIONS}
        showFeedback={showFeedback}
        isAutoScrollActive={isAutoScrollActive}
        workingPanel={workingPanel}
      />
      <div className='px-4 pt-4 pb-4'>
        <TurnAssetDrawer messages={messages} />
        <PromptInputWithActions
          isLoading={isLoading || isAgentPromptCommandsLoading}
          onSendMessage={sendMessage}
          placeholder="Type message or press '/' for commands"
          suggestions={agentPromptCommands?.items ?? NO_SUGGESTIONS}
          enableVoice={enableVoice}
          onSendVoiceNote={onSendVoiceNote}
          voiceCall={voiceCall}
          // A voice turn ends with the call, not the stop button.
          onAbort={
            isLoading && (!voiceCall || voiceCall.status === 'off')
              ? onAbort
              : undefined
          }
        />
      </div>
    </ChatSessionSwitcher>
  );
};

type ConnieStreamingChatType = typeof ChatType.connie | typeof ChatType.job;

type ConnieStreamingChatProps = Omit<CustomLangflowChatProps, 'chatType'> & {
  chatType: ConnieStreamingChatType;
};

const ConnieStreamingChat: React.FC<ConnieStreamingChatProps> = ({
  sessionId,
  user,
  onMessage,
  onThinking,
  sessions,
  isSessionsMode,
  onSessionChangeClick,
  onReturnToChatClick,
  chatType,
  contactId,
  currentJobReference,
  isActive = true,
}) => {
  // Opening another session or a new chat ends a live call.
  const stopCallRef = useRef<() => void>(() => undefined);
  const handleSessionSwitch = useCallback(() => stopCallRef.current(), []);
  const {
    messages,
    isLoading,
    sendMessage,
    sendVoiceNote,
    getVoiceContext,
    handleVoiceMessage,
    endVoiceCall,
    abort,
    updateMessageRating,
    aiAgent,
  } = useConnieChat({
    chatType,
    sessionId,
    userId: user?.id,
    isSessionsMode,
    contactId,
    currentJobReference,
    onMessage,
    onThinking,
    onSessionSwitch: handleSessionSwitch,
  });

  // Hands-free voice is on Connie Global only for now (VH3_CONNECT #562).
  const call = useConnieVoiceCall({
    getContext: () => ({
      ...getVoiceContext(),
      ...(user?.name && { userName: user.name }),
      ...(user?._company?.name && { userCompanyName: user._company.name }),
    }),
    onMessage: handleVoiceMessage,
    onEnd: endVoiceCall,
  });
  stopCallRef.current = call.stop;
  const voiceCall: VoiceCallControl | undefined =
    chatType === ChatType.connie
      ? {
          status: call.status,
          mode: call.mode,
          level: call.level,
          caption: call.caption,
          onStart: () => void call.start(),
          onStop: call.stop,
          onEndTurn: call.endTurn,
        }
      : undefined;

  return (
    <ChatShell
      sessions={sessions}
      isSessionsMode={isSessionsMode}
      sessionId={sessionId}
      user={user}
      messages={messages}
      isLoading={isLoading}
      sendMessage={sendMessage}
      updateMessageRating={updateMessageRating}
      aiAgentId={aiAgent?.id}
      showFeedback={false}
      onSessionChangeClick={onSessionChangeClick}
      onReturnToChatClick={onReturnToChatClick}
      enableVoice
      onSendVoiceNote={sendVoiceNote}
      voiceCall={voiceCall}
      onAbort={abort}
      isAutoScrollActive={isActive}
      workingPanel
    />
  );
};

const AgentBlockingChat: React.FC<CustomLangflowChatProps> = ({
  jobId,
  chatType = ChatType.job,
  sessionId,
  user,
  onMessage,
  onThinking,
  sessions,
  isSessionsMode,
  onSessionChangeClick,
  onReturnToChatClick,
  isActive = true,
}) => {
  const {
    messages,
    isLoading,
    sendMessage,
    handleLastMessageProcessCompleted,
    updateMessageRating,
    aiAgent,
  } = useAgentChat({
    chatType,
    entityId: jobId,
    sessionId,
    user,
    onMessage,
    onThinking,
    sessions,
    isSessionsMode,
    onSessionChangeClick,
    onReturnToChatClick,
  });

  return (
    <ChatShell
      sessions={sessions}
      isSessionsMode={isSessionsMode}
      sessionId={sessionId}
      user={user}
      messages={messages}
      isLoading={isLoading}
      sendMessage={sendMessage}
      updateMessageRating={updateMessageRating}
      aiAgentId={aiAgent?.id}
      showFeedback={
        chatType === ChatType.document || chatType === ChatType.quote
      }
      onLastMessageProcessCompleted={handleLastMessageProcessCompleted}
      onSessionChangeClick={onSessionChangeClick}
      onReturnToChatClick={onReturnToChatClick}
      isAutoScrollActive={isActive}
    />
  );
};

const CustomLangflowChat: React.FC<CustomLangflowChatProps> = props => {
  const { chatType } = props;
  if (chatType === ChatType.connie || chatType === ChatType.job) {
    return <ConnieStreamingChat {...props} chatType={chatType} />;
  }

  return <AgentBlockingChat {...props} />;
};

export { CustomLangflowChat };
